import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { RequestStatus } from '../redux/utils';
import {
	fetchCountryDirectory,
	fetchSchools,
	searchSchools,
	scopeKey,
	searchKey,
	SEARCH_MIN_CHARS,
	SCHOOL_DIRECTORY_FLAG,
	SCHOOL_DIRECTORY_DEV_PREVIEW,
	DirectorySchool
} from '../redux/slices/schoolDirectory';

/**
 * Gate + data hook for the school picker (PTEC-576).
 *
 * `enabled` is false while the `school-directory-enabled` flag is off — the
 * editor then renders plain free text and NOTHING is fetched (ships dark).
 *
 * Two ways in (PTEC-643):
 *
 *  - `query` drives a cross-state name search and is the PRIMARY path. It needs
 *    only a country, because requiring a state and a type first made the whole
 *    feature undiscoverable: the field looked like the old free-text input and
 *    294,825 schools sat behind a combination nothing on screen asked for.
 *  - `subdivision` + `institutionType` still load a scoped list, now as an
 *    optional narrowing filter rather than a precondition.
 *
 * `query` is passed already debounced — this hook fetches whenever it changes.
 */
export const useSchoolDirectory = (
	country?: string,
	subdivision?: string,
	institutionType?: string,
	query?: string
) => {
	const dispatch = useAppDispatch();
	// Dev preview keeps the gate open locally (fixture-first, API fallback);
	// real builds are governed solely by the feature flag. See the slice.
	const enabled =
		useAppSelector((state) => Boolean(state.featureFlags.flags[SCHOOL_DIRECTORY_FLAG])) ||
		SCHOOL_DIRECTORY_DEV_PREVIEW;
	const countryEntry = useAppSelector((state) => (country ? state.schoolDirectory.byCountry[country] : undefined));
	const key = country && subdivision && institutionType ? scopeKey(country, subdivision, institutionType) : null;
	const scopeEntry = useAppSelector((state) => (key ? state.schoolDirectory.scopes[key] : undefined));

	const trimmedQuery = query?.trim() ?? '';
	const searchable = trimmedQuery.length >= SEARCH_MIN_CHARS;
	const sKey = country && searchable ? searchKey(country, trimmedQuery) : null;
	const searchEntry = useAppSelector((state) => (sKey ? state.schoolDirectory.searches[sKey] : undefined));

	useEffect(() => {
		if (enabled && country && !countryEntry) {
			dispatch(fetchCountryDirectory(country));
		}
	}, [enabled, country, countryEntry, dispatch]);

	useEffect(() => {
		if (enabled && countryEntry?.supported && country && subdivision && institutionType && !scopeEntry) {
			dispatch(fetchSchools({ country, subdivision, institutionType }));
		}
	}, [enabled, countryEntry?.supported, country, subdivision, institutionType, scopeEntry, dispatch]);

	useEffect(() => {
		if (enabled && countryEntry?.supported && country && searchable && !searchEntry) {
			dispatch(searchSchools({ country, query: trimmedQuery }));
		}
	}, [enabled, countryEntry?.supported, country, searchable, trimmedQuery, searchEntry, dispatch]);

	const scopedSchools: DirectorySchool[] = scopeEntry?.status === RequestStatus.SUCCESS ? scopeEntry.schools : [];
	const searchResults: DirectorySchool[] = searchEntry?.status === RequestStatus.SUCCESS ? searchEntry.schools : [];

	/**
	 * A live search wins over the scoped list. Both can be populated at once — the
	 * user may have set a state filter and then typed — and in that case what they
	 * typed is the more specific intent. The scoped list stays available so the
	 * filter alone still yields options with an empty box.
	 */
	const schools = searchable ? searchResults : scopedSchools;

	return {
		enabled,
		supported: enabled && countryEntry?.supported === true,
		subdivisions: countryEntry?.subdivisions ?? [],
		schools,
		searching: searchable,
		loadingSchools: searchable
			? searchEntry?.status === RequestStatus.PENDING
			: scopeEntry?.status === RequestStatus.PENDING
	};
};
